import React from "react";
import { Card, Button, Chip, StatusPill } from "../ui";
import { Calendar, contentIcon } from "../icons";
import CertificateButton from "./CertificateButton";
import {
  TYPE_LABEL,
  isOverdue,
  formatDueDate,
  openLabel,
} from "../../lib/learning";

/**
 * A single assigned item — module, lesson, video, assessment or SCORM course.
 *
 * The coloured top edge encodes the content type at a glance, so a wall of
 * cards is scannable by shape before it's read. Everything else on the card is
 * server state: type, status, percent, due date and availability all arrive
 * from `/api/assignment/me`.
 */

/** Type → accent, chosen so related things sit together tonally. */
const ACCENT = {
  "ppt-module": undefined, // brand
  "ppt-item": "warn",
  video: undefined,
  assessment: "success",
  scorm: undefined,
};

export const AssignedItemCard = ({ item, onOpen }) => {
  const overdue = isOverdue(item);
  const Icon = contentIcon(item.contentType);

  // A certificate is offered only where the backend can actually issue one —
  // completed modules and paths. Anything else would 404 on click.
  const canCertify =
    item.status === "completed" &&
    (item.contentType === "ppt-module" || item.contentType === "learning-path");

  return (
    <Card accent={ACCENT[item.contentType] ?? "brand"} className="ds-stack">
      <div style={{ padding: "var(--s-4) var(--s-5) var(--s-5)", display: "flex", flexDirection: "column", flex: 1 }}>
        <div className="ds-row-flex ds-between ds-gap-2">
          <Chip>{TYPE_LABEL[item.contentType] || item.contentType}</Chip>
          <StatusPill
            status={item.status}
            percent={item.percent}
            overdue={overdue}
          />
        </div>

        <h3 className="ds-h3 ds-mt-3" style={{ lineHeight: 1.4 }}>
          {item.title}
        </h3>

        {item.module && (
          <p className="ds-sub" style={{ marginTop: 4 }}>
            {item.module}
          </p>
        )}

        <p
          className="ds-sub ds-row-flex ds-gap-2"
          style={{
            marginTop: 8,
            color: overdue ? "var(--danger-ink)" : undefined,
            fontWeight: overdue ? 600 : undefined,
          }}
        >
          <Calendar size={14} />
          {item.dueDate
            ? `${overdue ? "Overdue — was due" : "Due"} ${formatDueDate(item.dueDate)}`
            : "No due date"}
        </p>

        <div className="ds-stack ds-gap-2 ds-mt-4" style={{ marginTop: "auto", paddingTop: "var(--s-4)" }}>
          <Button
            variant={item.status === "completed" ? "outline" : "primary"}
            icon={Icon}
            block
            disabled={!item.available}
            onClick={() => onOpen(item)}
          >
            {item.available ? openLabel(item.status) : "Content unavailable"}
          </Button>

          {canCertify && (
            <div style={{ textAlign: "center" }}>
              <CertificateButton
                variant="link"
                contentType={item.contentType}
                contentId={item.contentId}
                title={item.title}
              />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default AssignedItemCard;
